import React from "react";
import {
    Upload,
    Download,
    FileText,
    Trash2,
    Info,
    Loader2,
    AlertTriangle,
} from "lucide-react";

const XFinanceImportForm = ({
    colors,
    csvFile,
    loading,
    handleFileUpload,
    downloadTemplate,
    clearData,
    currentTemplate,
    getHeadersForType
}) => {
    return (
        <>
            {/* Instrucciones */}
            <div className={`${colors.bg} ${colors.border} border rounded-xl p-6 mb-8`}>
                <h3 className={`text-lg font-semibold ${colors.text} mb-4 flex items-center gap-2`}>
                    <Info className="w-5 h-5" />
                    Instrucciones de Importación
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className={`font-semibold ${colors.textTertiary} mb-2`}>Pasos para importar:</h4>
                        <ol className={`space-y-1 ${colors.textSecondary} text-sm`}>
                            <li>1. Descargue la plantilla CSV</li>
                            <li>2. Complete sus datos en el archivo</li>
                            <li>3. Suba el archivo completado</li>
                            <li>4. Revise los datos importados</li>
                            <li>5. Confirme la importación</li>
                        </ol>
                    </div>
                    <div>
                        <h4 className={`font-semibold ${colors.textTertiary} mb-2`}>Campos requeridos:</h4>
                        <ul className={`space-y-1 ${colors.textSecondary} text-sm`}>
                            <li>• <strong>tipo:</strong> "gasto" o "ingreso"</li>
                            <li>• <strong>descripcion:</strong> Descripción del registro</li>
                            <li>• <strong>categoria:</strong> Categoría existente</li>
                            <li>• <strong>cantidad:</strong> Monto (número) (o horas/tarifa para ingresos por horas)</li>
                            <li>• <strong>metodoPago:</strong> Para gastos</li>
                            <li>• <strong>metodoCobro:</strong> Para ingresos</li>
                            <li>• <strong>fecha:</strong> Formato YYYY-MM-DD</li>
                            <li>• <strong>tipoIngreso:</strong> unico, recurrente, por_horas</li>
                            <li>• <strong>horasTrabajadas:</strong> Si tipo es por_horas</li>
                            <li>• <strong>tarifaPorHora:</strong> Si tipo es por_horas</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Descargar Plantilla */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                        <div className={`w-8 h-8 ${colors.iconBg} rounded-lg flex items-center justify-center`}>
                            <Download className={`w-5 h-5 ${colors.iconColor}`} />
                        </div>
                        Paso 1: Descargar Plantilla
                    </h2>
                    <p className="text-slate-600 mb-4">
                        Descargue la plantilla CSV con ejemplos de datos para facilitar la importación.
                    </p>
                    <button
                        onClick={downloadTemplate}
                        className={`${colors.button} text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2`}
                    >
                        <Download className="w-5 h-5" />
                        Descargar Plantilla CSV
                    </button>
                </div>
            </div>

            {/* Subir Archivo */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Upload className="w-5 h-5 text-blue-600" />
                        </div>
                        Paso 2: Subir Archivo CSV
                    </h2>

                    <div className="space-y-4">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                            <input
                                type="file"
                                id="csvFile"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={loading}
                            />
                            <label
                                htmlFor="csvFile"
                                className="cursor-pointer flex flex-col items-center gap-3"
                            >
                                {loading ? (
                                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                                ) : (
                                    <FileText className="w-12 h-12 text-slate-400" />
                                )}
                                <div>
                                    <p className="text-lg font-semibold text-slate-900">
                                        {loading ? "Procesando archivo..." : "Haga clic para seleccionar archivo CSV"}
                                    </p>
                                    <p className="text-slate-500">
                                        {csvFile ? `Archivo seleccionado: ${csvFile.name}` : "O arrastre el archivo aquí"}
                                    </p>
                                </div>
                            </label>
                        </div>

                        {csvFile && !loading && (
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-slate-600" />
                                    <div>
                                        <p className="font-medium text-slate-900">{csvFile.name}</p>
                                        <p className="text-sm text-slate-500">
                                            {(csvFile.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={clearData}
                                    className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                                    title="Eliminar archivo"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Información adicional */}
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Información Importante</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    <div>
                        <h4 className="font-semibold text-slate-800 mb-2">Formatos Soportados:</h4>
                        <ul className="space-y-1 text-slate-600">
                            <li>• <strong>Fecha:</strong> YYYY-MM-DD (ej: 2024-01-15)</li>
                            <li>• <strong>Cantidad:</strong> Números decimales (ej: 245.50)</li>
                            <li>• <strong>Etiquetas:</strong> Separadas por comas (ej: tag1,tag2)</li>
                            <li>• <strong>Codificación:</strong> UTF-8</li>
                            <li>• <strong>tipo:</strong> "gasto" o "ingreso"</li>
                            <li>• <strong>tipoIngreso:</strong> unico, recurrente, por_horas</li>
                            <li>• <strong>Horas/Tarifa:</strong> Solo para tipo por_horas</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-slate-800 mb-2">Validaciones:</h4>
                        <ul className="space-y-1 text-slate-600">
                            <li>• Las categorías deben existir previamente</li>
                            <li>• Los métodos de pago deben estar registrados</li>
                            <li>• Los métodos de cobro se validan automáticamente</li>
                            <li>• Las cantidades deben ser números positivos</li>
                            <li>• Las fechas deben ser válidas</li>
                            <li>• Para tipo "por_horas" se requieren horas y tarifa</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-amber-800 mb-1">Antes de importar:</h4>
                            <ul className="text-amber-700 text-sm space-y-1">
                                <li>• Asegúrese de que todas las categorías de gastos estén creadas</li>
                                <li>• Asegúrese de que todas las categorías de ingresos estén creadas</li>
                                <li>• Verifique que todos los métodos de pago estén registrados</li>
                                <li>• Revise que los datos no estén duplicados</li>
                                <li>• Haga una copia de seguridad de sus datos actuales</li>
                                <li>• Para ingresos por horas, verifique el cálculo automático</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default XFinanceImportForm;